/**
 * 005-packing-verification, admin-packing-oversight bucket. T053 — live packing status from
 * `GET /oversight/tasks`, and T059 — assign/unassign wired directly into this list's own columns
 * (`columns.tsx`'s `AssignCell`), per the partitioning note that T059 lands on T053's own file.
 *
 * vendor/isomorphic/apps/isomorphic/src/app/shared/appointment/appointment-list/stats.tsx
 *   the stage cards above the table (`MetricCard` with a solid icon tile), each a link to its status;
 * vendor/isomorphic/apps/isomorphic/src/app/shared/ecommerce/order/order-list/table.tsx
 *   the table card itself, through `components/table/data-table.tsx`'s `PagedTable`.
 *
 * No pagination on this route (verified against `PackingStore.TasksAsync`: `status?` only, no
 * page/pageSize), so the whole set arrives in one response and `PagedTable` pages it in memory
 * (its whole-set mode) — which also makes the search box and the header sorts honest: they cover
 * every task. The loader reads the list unfiltered once and the status dropdown and stage cards
 * narrow it here, so each count is the number of rows the API itself returned in that status.
 *
 * "Live" (2026-09-29, UI/UX pass): the loader re-runs every 30 s while the tab is visible and on
 * returning to it, and the toolbar says when the list was last read. Tasks flagged for review or
 * holding an unresolved conflict sort first — they are the ones someone has to act on.
 */
import { useEffect, useState } from "react";
import { Link, useLoaderData, useOutletContext, useRevalidator, useSearchParams, type LoaderFunctionArgs } from "react-router-dom";
import {
  PiArrowsClockwiseBold as RefreshIcon,
  PiCheckCircleDuotone as CompleteIcon,
  PiHandPointingDuotone as UnassignedIcon,
  PiPackageDuotone as InProgressIcon,
  PiUserCheckDuotone as AssignedIcon,
  PiWarningDuotone as ConflictIcon,
} from "react-icons/pi";
import { Alert, Button, Text } from "rizzui";
import { ErrorSurface, EmptyState, MetricCard, cn } from "@steelyard/theme";
import { PagedTable, type StatusOption } from "../../components/table/data-table";
import { ListHeader } from "../../components/table/list-header";
import { TableSearch } from "../../components/table/toolbar";
import { can } from "../../lib/permissions";
import { formatTime } from "../../lib/format";
import { hrefWith } from "../../lib/paging";
import { getOversightTasks, type PackingTaskRow, type PackingTaskStatus } from "../../lib/services/warehouse";
import type { AccountSummary, PermissionState } from "../../types";
import { listPackers } from "../../lib/services/identity";
import { PackersContext, taskColumns, type PackerDirectory } from "./columns";

const STATUSES: PackingTaskStatus[] = ["unassigned", "assigned", "in_progress", "complete", "conflicted", "cancelled"];
const statusLabel = (status: string) => status.charAt(0).toUpperCase() + status.slice(1).replaceAll("_", " ");
const REFRESH_MS = 30_000;

/** The stages a supervisor watches, in the order work moves through them. Cancelled stays in the dropdown only. */
const STAGES: { status: PackingTaskStatus; hint: string; icon: typeof UnassignedIcon; tile: string }[] = [
  { status: "unassigned", hint: "Waiting to be assigned", icon: UnassignedIcon, tile: "bg-orange text-white" },
  { status: "assigned", hint: "On someone's device, not started", icon: AssignedIcon, tile: "bg-blue text-white" },
  { status: "in_progress", hint: "Being packed now", icon: InProgressIcon, tile: "bg-primary text-white" },
  { status: "conflicted", hint: "Needs a decision", icon: ConflictIcon, tile: "bg-red text-white" },
  { status: "complete", hint: "Packed", icon: CompleteIcon, tile: "bg-green text-white" },
];

export async function packingTasksLoader({ request }: LoaderFunctionArgs) {
  const status = (new URL(request.url).searchParams.get("status") as PackingTaskStatus | null) ?? "";
  return { all: await getOversightTasks(), status, loadedAt: new Date().toISOString() };
}

const needsAttention = (task: PackingTaskRow) => task.flaggedForReview || task.hasUnresolvedConflict;

export default function PackingTasksList() {
  const { all, status, loadedAt } = useLoaderData() as Awaited<ReturnType<typeof packingTasksLoader>>;
  // Not in the loader: Identity's user list is slow, and the task list is useful without it.
  const [packers, setPackers] = useState<PackerDirectory>(undefined);
  useEffect(() => {
    listPackers()
      .then((page) =>
        setPackers(
          new Map(
            page.items
              .filter((u) => !u.companyRef)
              .map((u) => [u.id, { name: u.displayName ?? u.email ?? "Unknown user", email: u.email ?? undefined }]),
          ),
        ),
      )
      .catch(() => setPackers(null));
  }, []);
  const { permissionState } = useOutletContext<{ account: AccountSummary; permissionState: PermissionState }>();
  const [searchParams] = useSearchParams();
  const revalidator = useRevalidator();

  // Live: re-read on an interval while the tab is visible, and straight away on coming back to it.
  // A refetch already in flight is left alone rather than stacked.
  useEffect(() => {
    const refresh = () => {
      if (document.visibilityState === "visible" && revalidator.state === "idle") revalidator.revalidate();
    };
    const timer = window.setInterval(refresh, REFRESH_MS);
    document.addEventListener("visibilitychange", refresh);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", refresh);
    };
  }, [revalidator]);

  if (!can(permissionState, "packing.oversight")) {
    return (
      <ErrorSurface
        error={{ title: "Not permitted", status: 403, detail: "Live packing status is gated by packing.oversight, which your role does not hold." }}
        action="view live packing status"
      />
    );
  }

  // `?orderId=` — the order detail's "Assign packer" button. That order's task stays in the grid
  // with the rest, pinned first and highlighted, so it is plain which row to act on; assigning
  // revalidates without changing the URL, so it stays highlighted afterwards.
  const orderId = searchParams.get("orderId");
  const target = orderId ? all.find((task) => task.orderId === orderId) : undefined;
  const tasks = all
    .filter((task) => !status || task.status === status || task === target)
    // Stable: the API's own order within each group.
    .sort((a, b) => Number(b === target) - Number(a === target) || Number(needsAttention(b)) - Number(needsAttention(a)));
  const statusHref = (next?: string) => hrefWith("/packing", searchParams, { status: next, page: undefined });
  const countOf = (s: PackingTaskStatus) => all.filter((task) => task.status === s).length;
  const statusOptions: StatusOption[] = [
    { label: "All tasks", href: statusHref(undefined), active: !status, count: all.length },
    ...STATUSES.map((s) => ({ label: statusLabel(s), href: statusHref(s), active: s === status, count: countOf(s), status: s })),
  ];
  const refreshing = revalidator.state !== "idle";

  return (
    <div className="flex flex-col gap-6">
      <ListHeader
        title="Live Packing Status"
        count={tasks.length}
        description="Every packing task, who has it and anything flagged. Assigning or unassigning takes effect on that person's device at its next refresh."
        breadcrumb={[{ name: "Home", href: "/" }, { name: "Packing" }, { name: "Live Status" }]}
      />

      {/* Each stage card is its own filter: click to see only that stage, click again for every task. */}
      <nav aria-label="Tasks by stage" className="grid grid-cols-2 gap-4 md:grid-cols-3 xl:grid-cols-5">
        {STAGES.map(({ status: stage, hint, icon: Icon, tile }) => {
          const selected = status === stage;
          return (
            <Link key={stage} to={statusHref(selected ? undefined : stage)} aria-current={selected ? "true" : undefined} className="rounded-lg">
              <MetricCard
                title={statusLabel(stage)}
                metric={countOf(stage)}
                icon={<Icon aria-hidden className="size-6" />}
                iconClassName={cn(tile, "shadow-sm")}
                info={<Text className="mt-0.5 text-xs text-gray-500">{hint}</Text>}
                rounded="lg"
                className={cn("h-full transition-shadow hover:shadow-md", selected && "ring-2 ring-primary")}
                metricClassName="tabular-nums"
              />
            </Link>
          );
        })}
      </nav>

      {orderId ? (
        target ? (
          <Alert color="info" variant="flat">
            <Text>
              Choose who packs <strong>{target.orderNumber}</strong> — its row is highlighted at the top. Pick them
              in the Assignment column and press Assign; it reaches their device on its next refresh.
            </Text>
          </Alert>
        ) : (
          <Alert color="warning" variant="flat">
            <Text>
              This order has no packing task, so there is no one to assign yet. A task is opened when the order is
              approved — if it was approved and still has none, Warehouse could not be reached at that moment.
            </Text>
          </Alert>
        )
      ) : null}

      <PackersContext.Provider value={packers}>
        <PagedTable<PackingTaskRow>
          result={{ items: tasks, page: 1, pageSize: tasks.length, totalCount: tasks.length }}
          caption="Packing tasks, with status, lines, flags and assignment"
          columns={taskColumns(() => revalidator.revalidate())}
          getRowId={(row) => row.id}
          rowClassName={(row) =>
            row === target
              ? "bg-primary-lighter/70 [&>td:first-child]:border-l-4 [&>td:first-child]:border-l-primary"
              : needsAttention(row)
                ? "bg-red-lighter/30"
                : undefined
          }
          status={{ label: "Status", options: statusOptions }}
          search={(table) => <TableSearch table={table} placeholder="Search orders…" />}
          clear={{ active: Boolean(orderId || status), href: hrefWith("/packing", searchParams, { orderId: undefined, status: undefined, page: undefined }) }}
          actions={
            <>
              <Text as="span" className="text-xs text-gray-500" aria-live="polite">
                {refreshing ? "Refreshing…" : `Updated ${formatTime(loadedAt)}`}
              </Text>
              <Button variant="outline" onClick={() => revalidator.revalidate()} disabled={refreshing}>
                <RefreshIcon aria-hidden className={cn("me-1.5 size-4", refreshing && "animate-spin")} />
                Refresh
              </Button>
            </>
          }
          empty={
            <EmptyState title="No packing tasks">
              {status ? `Nothing is currently "${status.replaceAll("_", " ")}".` : "No packing tasks exist yet."}
            </EmptyState>
          }
        />
      </PackersContext.Provider>
    </div>
  );
}
